// Global
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';

// Components
import FilterForm from '../../components/FilterForm/FilterForm';
import { IFormInstance, showSuccessToast } from '@/components';
import { Skeleton } from '@/ui';

// Helpers
import { categoriesSelector, createFilter, editFilter, getFilters, selectFiltersQuery } from '../../feature';
import CreateFilterDTO from '../../dtos/CreateFilterDTO';
import { GetFiltersDTO } from '../../dtos';
import { TGetFiltersQuery } from '../../helpers';

type TFilterFormContainer = {
  form: IFormInstance;
  onSubmit: () => void;
  id?: string;
};

const FilterFormContainer = ({ form, onSubmit, id }: TFilterFormContainer) => {
  const { t } = useTranslation(['categories']);
  const isEdit = !!id;
  const { filterDetails, isLoadingFilterDetails } = useAppState(categoriesSelector);
  const query = useAppState(selectFiltersQuery);
  const dispatch = useAppDispatch();

  const handleSubmit = useCallback(async () => {
    try {
      const values = form.getFieldsValue();
      const filterDetailsDto = new CreateFilterDTO(values);

      if (isEdit) {
        await dispatch(editFilter({ id, data: filterDetailsDto })).unwrap();
      } else {
        await dispatch(createFilter(filterDetailsDto)).unwrap();
      }

      const dto = new GetFiltersDTO(query as TGetFiltersQuery);
      await dispatch(getFilters(dto));

      showSuccessToast({ message: isEdit ? t('filter_edit_success') : t('filter_create_success') });
      onSubmit();
    } catch {}
  }, [onSubmit, form, query]);

  if (isLoadingFilterDetails) return <Skeleton />;

  return <FilterForm onSubmit={handleSubmit} formInstance={form} isEdit={isEdit} initialValues={filterDetails} />;
};

export default FilterFormContainer;
